import * as THREE from "three";
import { TextureLoader } from "three";

import materialsMap from "../../data/materials";
import FlakesTexture from "../../assets/jsm/FlakesTexture";
import clearNormal from "../../assets/textures/clear-normal.jpeg";
import pkg from "../../../package.json";

export default class MaterialManager {
  constructor(scene, renderer) {
    this.basepath = (pkg.homepage || "") + "/textures";
    this.debug = false;

    this.scene = scene;
    this.render = renderer;

    this.materials = {};

    const textureLoader = new THREE.TextureLoader();
    this.normalMap = textureLoader.load(clearNormal);
    this.normalMap.wrapS = THREE.RepeatWrapping;
    this.normalMap.wrapT = THREE.RepeatWrapping;

    this.normalMapFlake = new THREE.CanvasTexture(new FlakesTexture());
    this.normalMapFlake.wrapS = THREE.RepeatWrapping;
    this.normalMapFlake.wrapT = THREE.RepeatWrapping;
    this.normalMapFlake.repeat.x = 10;
    this.normalMapFlake.repeat.y = 6;
    this.normalMapFlake.anisotropy = 16;

    this.HDR = null;

    this._createMaterial(
      "_MISSING", "#FF0000", null, false, 0.9, 0.5, null, 1.0, 1 , null
    ).then((material) => {
      this.materials._MISSING = material;
    });

    // this._showMaterialReferences();    
  }

  /**
   * Create Material Grid
   * @param {image} hdr - set HDRI Image
   */
  setHdr(hdr) {
    this.HDR = hdr;

    if (!this.materials.length) {
      return;
    }

    for (const key in this.materials) {
      if (this.materials.hasOwnProperty(key)) {
        const mat = this.materials[key];
        mat.envMap = hdr;
        mat.envMapIntensity = 1.1;      
        mat.needsUpdate = true;
          
      }
    }
  };

  getMaterialByName(name) {
    return new Promise((resolve, reject) => {
      if (this.materials.hasOwnProperty(name)) {
        return resolve(this.materials[name]);
      }
  
      if (!materialsMap.MATERIALS.hasOwnProperty(name)) {
        console.warn(`Material "${name}" is not defined`);
        return resolve(this.materials["_MISSING"]);
      }
  
      const data = materialsMap.MATERIALS[name];
      this.debug && console.log('data:', data);

      this._createMaterial(
        name,
        data.color,
        data.texture,
        data.carPaint,
        data.metalness,
        data.roughness,
        data.normals,
        data.transparency,
        data.tile,
        data.alphaMap
      ).then((material) => {

        this.materials[name] = material;
        resolve(this.materials[name]);
      });  
    });
  }

  /**
   * Create the material
   * @param {string} name - Name of the material
   * @param {#} colour - Material color
   * @param {bool} useCarPaint - Add the flakes of car paint
   * @param {float} metalness - Metalnes
   * @param {float} roughness - Roughness of the material
   */
  _createMaterial(name, colour, texture, useCarPaint, metalness, roughness, normalPath, transparency, tile , alphaMap) {
    const self = this;

    

    return new Promise((resolve, reject) => {
      let diffuse;
      let normal;
      let alpha;
      let textureCount = 0;
      const hasDiffuseMap = (texture && texture.length > 0);
      const hasNormalMap = (normalPath && normalPath.length > 0);
      const hasAlphaMap = (alphaMap && alphaMap.length > 0);

      if (hasDiffuseMap) textureCount++;
      if (hasNormalMap) textureCount++;
      if (hasAlphaMap) textureCount++;

      const onTextureLoaded = () => {
        textureCount--;
        if (textureCount === 0) {
          const material = onComplete();
          resolve(material);
        }
      }
      
      const onComplete = () => {
        const material = new THREE.MeshPhysicalMaterial({
          transparent: transparency < 1.0 ? true : false,
          opacity: transparency,
          //clearcoat: 0.8,
          //clearcoatRoughness: roughness,
          map: diffuse || null,
          metalness: metalness,
          roughness: roughness,
          color: colour,
          envMap: this.HDR,
          envMapIntensity: 0.4,
          roughnessMap: null,
          normalMap: useCarPaint ? this.normalMapFlake : normal,
          // clearcoatNormalMap: useCarPaint ? this.normalMapFlake : normal
        });

        if(alpha)
          material.alphaMap = alpha;

        material.name = name;

        return material;
      }

      if (textureCount === 0) {
        normal = this.normalMap;
        const material = onComplete();
        return resolve(material);
      }
  
      if (hasAlphaMap) {
        
        new TextureLoader().load(
          `${this.basepath}/${alphaMap}`,
          function(texture) {
            alpha = texture;
            if (tile !== 1) {
              alpha.wrapS = THREE.RepeatWrapping;
              alpha.wrapT = THREE.RepeatWrapping;
              alpha.repeat = new THREE.Vector2(tile, tile);
            }


            alpha.flipY  = false;

            self.debug && console.log('alpha:', alpha);

            onTextureLoaded();
          },
          undefined,
          function(error) {
            console.error(name ," : " , alphaMap  , 'Alpha map loading error:', error);
            onTextureLoaded();
          }
        );
      }

      if (hasDiffuseMap) {
        

        new TextureLoader().load(
          `${this.basepath}/${texture}`,
          function(texture) {
            diffuse = texture;
            if (tile !== 1) {
              diffuse.wrapS = THREE.RepeatWrapping;
              diffuse.wrapT = THREE.RepeatWrapping;
              diffuse.repeat = new THREE.Vector2(tile, tile);
            }

            diffuse.flipY  = false;
              

            self.debug && console.log('diffuse:', diffuse);

            onTextureLoaded();
          },
          undefined,
          function(error) {
            console.error('Diffuse map loading error:', error);
            onTextureLoaded();
          }
        );
      }
  
      if (hasNormalMap) {
        new TextureLoader().load(
          `${this.basepath}/${normalPath}`, 
          function(texture) {
            normal = texture;
            normal.repeat.set(1, 1);
            if (tile !== 1) {
              normal.wrapS = THREE.RepeatWrapping;
              normal.wrapT = THREE.RepeatWrapping;
              normal.repeat = new THREE.Vector2(tile, tile);
            }

            normal.flipY  = false;

            self.debug && console.log('normalPath: ' , normal);

            onTextureLoaded();
          }, 
          undefined,
          function(error) {
            console.error('Normal map loading error:', error);
            onTextureLoaded();
          }
        );
      }
    });
  };

  /**
   * Create Material Grid
   */
  _showMaterialReferences = () => {
    let lin = 0;
    let col = 0;

    const materials = [];

    for(const key  in materialsMap.MATERIALS){

      const m =  materialsMap.MATERIALS[key]
      const material = this._createMaterial(
        key,
        m.color,
        m.texture,
        m.carPaint,
        m.metalness,
        m.roughness,
        m.normals,
        m.transparency,
        m.tile,
        m.alphaMap
      );
      materials.push(material);
    }

    materials.forEach((m, i) => {
      if (lin !== 0 && lin % 8 === 0) {
        lin = 0;
        col++;
      } else lin++;

      const px = -0.4 + lin * 0.1;
      const py = 0.2 + col * 0.1;

      const geometry = new THREE.SphereBufferGeometry(0.04, 32, 32);
      this.sphere = new THREE.Mesh(geometry, m);
      this.sphere.position.set(px, py, -0.4);
      this.scene.add(this.sphere);
    });
  };
}
